import type { NumberLocale } from './money';

/** A calendar date as 'YYYY-MM-DD' and a budget month as 'YYYY-MM'. Both compare correctly as strings. */
export type IsoDate = string;
export type Month = string;

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const MONTHS_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const WEEKDAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAYS_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const pad = (n: number) => String(n).padStart(2, '0');

export const monthOf = (date: IsoDate): Month => date.slice(0, 7);

export function addMonths(month: Month, n: number): Month {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const total = y * 12 + (m - 1) + n;
  return `${Math.floor(total / 12)}-${pad((total % 12) + 1)}`;
}

/** Inclusive number of months from a to b (a ≤ b → ≥ 1). */
export function monthsBetween(a: Month, b: Month): number {
  const [ya, ma] = a.split('-').map(Number) as [number, number];
  const [yb, mb] = b.split('-').map(Number) as [number, number];
  return yb * 12 + mb - (ya * 12 + ma);
}

export function daysInMonth(month: Month): number {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

export const firstDay = (month: Month): IsoDate => `${month}-01`;
export const lastDay = (month: Month): IsoDate => `${month}-${pad(daysInMonth(month))}`;

export function addDays(date: IsoDate, n: number): IsoDate {
  const d = new Date(date + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export function daysBetween(a: IsoDate, b: IsoDate): number {
  return Math.round((Date.parse(b + 'T00:00:00Z') - Date.parse(a + 'T00:00:00Z')) / 86_400_000);
}

export function weekday(date: IsoDate): number {
  return new Date(date + 'T00:00:00Z').getUTCDay();
}

/** Number of given weekdays (0 = Sun … 6 = Sat) that fall within the month. */
export function countWeekdays(month: Month, wd: number): number {
  let n = 0;
  const days = daysInMonth(month);
  for (let d = 1; d <= days; d++) if (weekday(`${month}-${pad(d)}`) === wd) n++;
  return n;
}

/** Today's date in a given IANA time zone (default Europe/Belgrade), as an IsoDate. */
export function todayIn(timeZone = 'Europe/Belgrade', now: Date = new Date()): IsoDate {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
    .formatToParts(now)
    .reduce<Record<string, string>>((acc, p) => ((acc[p.type] = p.value), acc), {});
  return `${parts.year}-${parts.month}-${parts.day}`;
}

/** "02.11." (sr-Latn) / "Nov 2" (en). */
export function formatDateShort(date: IsoDate, locale: NumberLocale): string {
  const [, m, d] = date.split('-').map(Number) as [number, number, number];
  return locale === 'sr-Latn' ? `${pad(d)}.${pad(m)}.` : `${MONTHS_SHORT[m - 1]} ${d}`;
}

/** "02.11.2026" / "Nov 2, 2026". */
export function formatDateLong(date: IsoDate, locale: NumberLocale): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return locale === 'sr-Latn' ? `${pad(d)}.${pad(m)}.${y}` : `${MONTHS_SHORT[m - 1]} ${d}, ${y}`;
}

/** "Nov 2026" */
export function formatMonthShort(month: Month): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return `${MONTHS_SHORT[m - 1]} ${y}`;
}

/** "November 2026" */
export function formatMonthLong(month: Month): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return `${MONTHS_LONG[m - 1]} ${y}`;
}

/** "November" */
export function monthName(month: Month, short = false): string {
  const m = Number(month.slice(5, 7));
  return (short ? MONTHS_SHORT : MONTHS_LONG)[m - 1]!;
}

/** "Today" / "Yesterday" / "Sat, 31.10." */
export function dayLabel(date: IsoDate, today: IsoDate, locale: NumberLocale): string {
  if (date === today) return 'Today';
  if (date === addDays(today, -1)) return 'Yesterday';
  return `${WEEKDAYS_SHORT[weekday(date)]}, ${formatDateShort(date, locale)}`;
}

/** "Monday, 2 November" */
export function formatDateHeading(date: IsoDate): string {
  const [, m, d] = date.split('-').map(Number) as [number, number, number];
  return `${WEEKDAYS_LONG[weekday(date)]}, ${d} ${MONTHS_LONG[m - 1]}`;
}
